import * as d3 from "d3";

const moment = require("moment-timezone");
const DATETIME_FORMAT = moment.tz.guess().split("/")[0] == "America" ? "MM/DD/YYYY, HH:mm" : "DD/MM/YYYY, HH:mm";
const TIMESINCE = (seconds) => {
  var interval = Math.floor(seconds / 31536000);
  if (seconds == -1) {
    return "None";
  }
  if (interval > 1) {
    return interval + " years";
  }
  interval = Math.floor(seconds / 2592000);
  if (interval > 1) {
    return interval + " months";
  }
  interval = Math.floor(seconds / 86400);
  if (interval > 1) {
    return interval + " days";
  }
  interval = Math.floor(seconds / 3600);
  if (interval > 1) {
    return interval + " hours";
  }
  interval = Math.floor(seconds / 60);
  if (interval > 1) {
    return interval + " minutes";
  }
  if (seconds > 1) {
    return Math.floor(seconds) + " seconds";
  }
  if (seconds != 0) return Math.floor(seconds * 1000) + " ms";
  else return "-";
};

export const D3Utility = {
  create_svg: function (id, chart_config) {
    return d3
      .select(`div#${id}`)
      .append("svg")
      .attr("viewBox", [0, 0, chart_config.width, chart_config.height + chart_config.legendHeight])
      .classed("svg-content", true);
  },
  append_clip_path: function (chart_config, clip, svg) {
    svg
      .append("clipPath")
      .attr("id", clip.id)
      .append("rect")
      .attr("x", chart_config.margin)
      .attr("y", chart_config.margin)
      .attr("width", chart_config.width - 2 * chart_config.margin - 2 * chart_config.padding)
      .attr("height", chart_config.height - 2 * chart_config.margin - chart_config.padding);
    return svg;
  },
  append_clip_polygon_path: function (clip, svg) {
    svg
      .append("clipPath")
      .attr("id", clip.id)
      .append("polygon")
      .attr("fill", "yellow")
      .attr("points", "30,275 30,75 230,40 530,100 530,400 300,500 ");
    return svg;
  },
  append_axis: function (svg, axis, scale, x, y) {
    return svg
      .append("g")
      .attr("class", "axis")
      .attr("transform", "translate(" + x + "," + y + ")")
      .call(axis, scale);
  },
  get_line_obj: function (xScale, yScale, curve = "monotone") {
    let curve_type = curve == "natural" ? d3.curveNatural : d3.curveMonotoneX;
    return d3
      .line()
      .curve(curve_type)
      .x(function (d) {
        return xScale(d.key);
      })
      .y(function (d) {
        return yScale(d.value);
      });
  },
  get_area_obj: function (xScale, yScale) {
    return d3
      .area()
      .curve(d3.curveMonotoneX)
      .x(function (d) {
        return xScale(d.key);
      })
      .y1(function (d) {
        return yScale(d.value);
      });
  },
  define_zoom: function (chart_config, zoomed, zoom_end) {
    return d3
      .zoom()
      .scaleExtent([0.5, 32])
      .extent([
        [0, 0],
        [chart_config.width - chart_config.margin, chart_config.height - (chart_config.margin + chart_config.padding)],
      ])
      .on("zoom", zoomed)
      .on("end", zoom_end);
  },
  define_restricted_zoom: function (chart_config, zoomed) {
    return d3
      .zoom()
      .scaleExtent([1, 12])
      .extent([
        [chart_config.margin, 0],
        [chart_config.width - chart_config.margin, chart_config.height - chart_config.margin - chart_config.padding],
      ])
      .translateExtent([
        [chart_config.margin, -Infinity],
        [chart_config.width - chart_config.margin, Infinity],
      ])
      .on("zoom", zoomed);
  },
  brushing: function (svg, chart_config, updateChart) {
    return svg.call(
      d3
        .brushX()
        .extent([
          [chart_config.margin, chart_config.margin],
          [chart_config.width - chart_config.margin, chart_config.height - chart_config.margin - chart_config.padding],
        ])
        .on("start end", updateChart)
    );
  },
  append_lines: function (
    svg,
    data,
    chart_config,
    clip,
    line,
    color,
    xScale,
    yLineScale,
    div,
    chart_data,
    no_pointers = false,
    min = null,
    max = null
  ) {
    let id = chart_data.id;
    let self = this;
    return svg
      .selectAll("lines")
      .data(data)
      .enter()
      .append("g")
      .attr("transform", "translate(" + chart_config.padding + ",0)")
      .attr("class", `lines`)
      .each(function (d) {
        let label = d.label;
        let suffix = d.suffix || chart_data.suffix;
        self.append_path(this, d, clip, color, line, id, chart_data.strokeWidth);
        if (!no_pointers)
          self.append_pointers(
            this,
            d,
            clip,
            xScale,
            yLineScale,
            label,
            div,
            suffix,
            chart_config,
            id,
            chart_data.strokeWidth
          );
        else self.append_min_max(this, d, clip, xScale, yLineScale, label, div, suffix, chart_config, id, min, max);
      });
  },
  append_path: function (ref_elem, data, clip, color, line, id, width) {
    return d3
      .select(ref_elem)
      .append("path")
      .attr("clip-path", clip)
      .attr("id", "line-" + data.id)
      .attr("class", "")
      .attr("fill", "none")
      .attr("stroke", color(data.id))
      .attr("stroke-width", width ? width : 2) // Line stroke-width
      .attr("d", function (d) {
        return line(d.values);
      })
      .on("mouseover", function () {
        // on mouse in show line, circles and text
        d3.select(`#${id} .line-axis`).node() && d3.select(`#${id} .line-axis`).node().classList.toggle("highlight");
        d3.select(`#${id} .axis`).node() && d3.select(`#${id} .axis`).node().classList.toggle("highlight");
      })
      .on("mouseout", function () {
        d3.select(`#${id} .line-axis`).node() && d3.select(`#${id} .line-axis`).node().classList.toggle("highlight");
        d3.select(`#${id} .axis`).node() && d3.select(`#${id} .axis`).node().classList.toggle("highlight");
      });
  },
  append_pointers: function (ref_elem, data, clip, xScale, yLineScale, label, div, suffix, chart_config, id) {
    let grid_line = d3.select(ref_elem).append("g").attr("class", "grid-lines");
    return d3
      .select(ref_elem)
      .append("g")
      .attr("class", `points pointer`)
      .attr("id", `points-${data.id}`)
      .selectAll("g")
      .data(data.values)
      .enter()
      .append("circle")
      .attr("class", "point pointer")
      .attr("clip-path", clip)
      .attr("fill-opacity", 0.5)
      .attr("r", 2.5) // Dot size
      .attr("cx", (d) => xScale(d ? d.key : null))
      .attr("cy", (d) => yLineScale(d ? d.value : null))
      .on("mouseover", function (event, elem) {
        let coords = d3.pointer(event);
        grid_line
          .append("line")
          .attr("class", "grid-line")
          .style("stroke", "grey")
          .style("stroke-width", 2)
          .attr("x1", coords[0])
          .attr("y1", coords[1])
          .attr("x2", chart_config.margin)
          .attr("y2", coords[1]);
        grid_line
          .append("line")
          .attr("class", "grid-line")
          .style("stroke", "grey")
          .style("stroke-width", 2)
          .attr("x1", coords[0])
          .attr("y1", coords[1])
          .attr("x2", coords[0])
          .attr("y2", chart_config.height - chart_config.margin - chart_config.padding);
        div.transition().duration(200).style("opacity", 1);
        div
          .html(
            `<div>
              ${label}
              </div><div class='primaryGrey--text caption'>
              ${elem.key}
              </div><div class='font-weight-bold'>
              ${
                elem.value
                  ? typeof elem.value == "number"
                    ? elem.value.toFixed(2)
                    : parseFloat(elem.value).toFixed(2)
                  : "-"
              }
              
                ${suffix ? suffix : ""} </div>`
          )
          .style("left", event.pageX - 100 + "px")
          .style("top", event.pageY - 120 + "px");
        d3.select(`#${id} .line-axis`).node() && d3.select(`#${id} .line-axis`).node().classList.toggle("highlight");
        d3.select(`#${id} .axis`).node() && d3.select(`#${id} .axis`).node().classList.toggle("highlight");
      })
      .on("mouseout", function () {
        div.transition().duration(500).style("opacity", 0);
        d3.select(`#${id} .line-axis`).node() && d3.select(`#${id} .line-axis`).node().classList.toggle("highlight");
        d3.select(`#${id} .axis`).node() && d3.select(`#${id} .axis`).node().classList.toggle("highlight");
        d3.selectAll(".grid-line").remove();
      });
  },
  append_min_max: function (ref_elem, data, clip, xScale, yLineScale, label, div, suffix, chart_config, id, min, max) {
    let min_count = 0;
    let max_count = 0;
    return d3
      .select(ref_elem)
      .selectAll(".min-max-con")
      .data(data.values)
      .enter()
      .append("g")
      .attr("class", `min-max-con`)
      .each(function (d) {
        if (
          (yLineScale(d.value) == yLineScale(min) && min_count == 0) ||
          (yLineScale(d.value) == yLineScale(max) && max_count == 0)
        ) {
          yLineScale(d.value) == yLineScale(min) ? min_count++ : max_count++;
          d3.select(this)
            .append("line")
            .attr("class", "point pointer")
            .attr("clip-path", clip)
            .attr("stroke", yLineScale(d.value) == yLineScale(max) ? "#fe4503" : "#f7af5d")
            .style("stroke-width", 8)
            .style("opacity", 0.5)
            .attr("x1", xScale(d.key))
            .attr("y1", yLineScale(d.value) - 25)
            .attr("x2", xScale(d.key))
            .attr("y2", yLineScale(d.value) + 25);

          d3.select(this)
            .append("text")
            .attr("class", "text")
            // .attr("clip-path", clip)
            .attr("transform", "translate(" + (xScale(d.key) - 25) + "," + (yLineScale(d.value) + 60) + ")")
            .attr("font-size", 45)
            .text(yLineScale(d.value));
        }
      });
  },
  append_bars: function (svg, data, chart_config, clip, barWidth, color, xScale, yBarScale, div, chart_data) {
    let id = chart_data.id;
    let self = this;
    return svg
      .selectAll(".bars")
      .data(data)
      .enter()
      .append("g")
      .attr("transform", "translate(" + chart_config.padding + ",0)")
      .attr("class", () => `bars`)
      .attr("id", (d) => `bars-${d.id}`)
      .each(function (d) {
        let label = d.label;
        let suffix = d.suffix || chart_data.suffix;
        self.append_rect(
          this,
          d,
          chart_config,
          clip,
          barWidth,
          color,
          xScale,
          yBarScale,
          div,
          label,
          d.id,
          data.length,
          suffix,
          id
        );
      });
  },
  append_events(svg, data, chart_config, clip, color, xScale, div) {
    let self = this;
    let grp = svg.append("g").attr("class", "events").attr("clip-path", clip);
    grp
      .selectAll(".event")
      .data(data)
      .enter()
      .append("g")
      .attr(
        "transform",
        (d) =>
          `translate(${xScale(new Date(d.timestampISO || d.key))},
          ${chart_config.height - (2 * chart_config.padding + chart_config.margin) + 10})`
      )
      .attr(
        "class",
        (d) =>
          `event ${
            d.eventType
              ? d.eventType == "LOSS_OF_MAINS"
                ? "critical"
                : d.eventType == "INTERRUPT"
                ? "warning"
                : d.eventType == "RAPID_VOLTAGE_CHANGE"
                ? "fault"
                : "info"
              : "info"
          }`
      )
      .attr("id", (d, i) => `event-${d.seqNo ? d.seqNo + "_" + i : d.id + "_" + i}`)
      .each(function (d) {
        let event_grp = d3.select(this);
        self.append_icon(event_grp);
        self.append_text(event_grp, d);
      })
      .on("mouseover", function (event, elem) {
        div.transition().duration(200).style("opacity", 1);
        div
          .html(
            `<div class='mb-2'> 
              ${elem.seqNo ? elem.seqNo : "-"}
               <div class='primaryGrey--text caption'> 
              ${elem.timestampISO ? moment(elem.timestampISO).format(DATETIME_FORMAT) : new Date(elem.key)}
              </div></div><div class='primaryGrey--text caption mb-2'> 
              ${elem.eventType ? elem.eventType : ""} 
              </div>`
          )
          .style("left", event.pageX - 100 + "px")
          .style("top", event.pageY - 150 + "px")
          .style("width", "300px")
          .style("height", "150px");
        d3.select(this).node() && d3.select(this).node().classList.toggle("highlight");
      })
      .on("mouseout", function () {
        d3.selectAll(".grid-line").remove();
        div.transition().duration(500).style("opacity", 0).style("width", "200px").style("height", "110px");
        d3.select(this).node() && d3.select(this).node().classList.toggle("highlight");
      });
    return grp;
  },
  append_icon: function (ref_elem) {
    return (
      ref_elem
        .append("polygon")
        .attr("class", `icon`)
        .attr("points", "0,10 10,5 10,-10, -10,-10 -10,5")
        .attr("fill", "white")
        // .style("stroke", "grey")
        .style("pointer-events", "visible")
        .style("stroke-width", 2)
    );
  },
  append_text: function (ref_elem, data) {
    return ref_elem
      .append("text")
      .attr("class", "icon-text")
      .attr("x", 0)
      .attr("y", 4)
      .style("pointer-events", "visible")
      .style("stroke-width", 0.8)
      .attr("text-anchor", "middle")
      .attr("font-size", 14)
      .text(data.eventType ? data.eventType[0] : "!");
  },
  append_rect: function (
    ref_elem,
    data,
    chart_config,
    clip,
    barWidth,
    color,
    xScale,
    yBarScale,
    div,
    label,
    id,
    len,
    suffix,
    chart_id
  ) {
    let barwidth = typeof barWidth == "function" ? barWidth.bandwidth() / 2 : barWidth * 30;
    let grid_line = d3.select(ref_elem).append("g").attr("class", "grid-lines");
    return d3
      .select(ref_elem)
      .selectAll("rect")
      .data(data.values)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("clip-path", clip)
      .attr("x", function (d, i) {
        let temp = xScale(d.key);
        return len > 1 ? temp - (barwidth / 2) * 2 * (i + 1) : temp - barwidth / 2;
      })
      .attr("y", function (d) {
        return yBarScale(d.value);
      })
      .attr("width", barwidth)
      .attr("height", function (d) {
        return chart_config.height - chart_config.margin - chart_config.padding - yBarScale(d.value);
      })
      .attr("fill", color(id))
      .on("mouseover", function (event, elem) {
        let coords = d3.pointer(event);
        grid_line
          .append("line")
          .attr("class", "grid-line")
          .style("stroke", "grey")
          .style("stroke-width", 2)
          .attr("x1", coords[0])
          .attr("y1", yBarScale(elem.value))
          .attr("x2", chart_config.width - (2 * chart_config.margin + chart_config.padding))
          .attr("y2", yBarScale(elem.value));

        div.transition().duration(200).style("opacity", 1);
        div
          .html(
            "<div>" +
              label +
              "</div><div class='primaryGrey--text caption'>" +
              elem.key +
              "</div><div class='font-weight-bold'>" +
              elem.value +
              (suffix ? suffix : "") +
              "</div>"
          )
          .style("left", event.pageX - 100 + "px")
          .style("top", event.pageY - 150 + "px");
        d3.select(`#${chart_id} .bar-axis`).node() &&
          d3.select(`#${chart_id} .bar-axis`).node().classList.toggle("highlight");
        d3.select(`#${chart_id} .axis`).node() && d3.select(`#${chart_id} .axis`).node().classList.toggle("highlight");
      })
      .on("mouseout", function () {
        d3.selectAll(".grid-line").remove();
        div.transition().duration(500).style("opacity", 0);
        d3.select(`#${chart_id} .bar-axis`).node() &&
          d3.select(`#${chart_id} .bar-axis`).node().classList.toggle("highlight");
        d3.select(`#${chart_id} .axis`).node() && d3.select(`#${chart_id} .axis`).node().classList.toggle("highlight");
      });
  },
  append_group: function (svg, x, y) {
    return svg.append("g").attr("transform", "translate(" + x + "," + y + ")");
  },
  append_legends: function (svg, id, x_pt, y_pt, keys, chart_config, color) {
    let hidden_Arr = [];
    let legends = this.append_group(svg, x_pt, y_pt);
    let width = -30;
    let y = 0;
    legends
      .selectAll("legend")
      .data(keys)
      .enter()
      .append("g")
      .attr("transform", (d, i) => {
        let label = null;
        if (i != 0) label = keys[i - 1].label ? keys[i - 1].label : keys[i - 1];
        let x = !label ? 0 : label.length <= 12 ? 140 : label.length <= 18 ? 180 : label.length <= 22 ? 200 : 220;
        width = width + x + chart_config.padding;
        if (width >= chart_config.width - 300) {
          width = 0;
          y = y + 30;
        }
        return "translate(" + width + "," + y + ")";
      })
      .attr("class", "legend")
      .each(function () {
        d3.select(this).attr("id", (d) => `legend-${id}-${d.id || d}`);
        d3.select(this)
          .append("circle")
          .attr("cx", 50)
          .attr("cy", function (d, i) {
            return 50 + i * 25;
          })
          .attr("r", 7)
          .style("fill", function (d) {
            let id = d.id ? d.id : d;
            return color(id);
          });
        d3.select(this)
          .append("circle")
          .attr("class", "selected")
          .attr("cx", 50)
          .attr("cy", function (d, i) {
            return 50 + i * 25;
          })
          .attr("r", 2.5)
          .style("fill", "#fff");

        d3.select(this)
          .append("text")
          .attr("x", 60)
          .attr("y", function (d, i) {
            return 50 + i * 25;
          })
          .style("fill", function (d) {
            let id = d.id ? d.id : d;
            return color(id);
          })
          .text(function (d) {
            return d.label ? d.label : d;
          })
          .attr("text-anchor", "left")
          .style("alignment-baseline", "middle");
      })
      .on("mouseover", function (event, d) {
        let label = d.id ? d.id : d;
        d3.selectAll(`div#${id} #line-${label}`).node() &&
          d3.selectAll(`div#${id} #line-${label}`).node().classList.add("shadow");
        d3.selectAll(`div#${id} #bars-${label} rect`) &&
          d3.selectAll(`div#${id} #bars-${label} rect`).attr("class", "bar shadow");
      })
      .on("mouseout", function (event, d) {
        let label = d.id ? d.id : d;
        d3.selectAll(`div#${id} #line-${label}`).node() &&
          d3.selectAll(`div#${id} #line-${label}`).node().classList.remove("shadow");
        d3.selectAll(`div#${id} #bars-${label} rect`) &&
          d3.selectAll(`div#${id} #bars-${label} rect`).attr("class", "bar");
      })
      .on("click", function (event, d) {
        let label = d.id ? d.id : d;
        hidden_Arr.indexOf(label) == -1 ? hidden_Arr.push(label) : hidden_Arr.splice(hidden_Arr.indexOf(label), 1);
        keys.map((elem) => {
          let e_id = elem.id || elem;
          if (hidden_Arr.indexOf(e_id) != -1) {
            d3.selectAll(`g#legend-${id}-${e_id}`).node().classList.add("disabled");
            d3.selectAll(`div#${id} #line-${e_id}`).node() &&
              d3.selectAll(`div#${id} #line-${e_id}`).node().classList.add("hide");
            d3.selectAll(`div#${id} g#points-${e_id}`).node() &&
              d3.selectAll(`div#${id} g#points-${e_id}`).node().classList.add("hide");
            d3.selectAll(`div#${id} #bars-${e_id}`).node() &&
              d3.selectAll(`div#${id} #bars-${e_id}`).node().classList.add("hide");
          } else {
            d3.selectAll(`g#legend-${id}-${e_id}`).node().classList.remove("disabled");
            d3.selectAll(`div#${id} #line-${e_id}`).node() &&
              d3.selectAll(`div#${id} #line-${e_id}`).node().classList.remove("hide");
            d3.selectAll(`div#${id} g#points-${e_id}`).node() &&
              d3.selectAll(`div#${id} g#points-${e_id}`).node().classList.remove("hide");
            d3.selectAll(`div#${id} #bars-${e_id}`).node() &&
              d3.selectAll(`div#${id} #bars-${e_id}`).node().classList.remove("hide");
          }
        });
      });
    return legends;
  },
  get_conatiner_width: function (selector) {
    return d3.select(selector).node().getBoundingClientRect().width;
  },
  get_scale_ordinal: function (keys, colorScheme) {
    return d3.scaleOrdinal().domain(keys).range(colorScheme);
  },
  get_linear_scale: function (arr) {
    return d3.scaleLinear().rangeRound(arr);
  },
  get_utc_scale: function (arr) {
    return d3.scaleUtc().range(arr);
  },
  get_scale_band: function (data_arr, arr) {
    return d3.scaleBand().domain(data_arr).range(arr).padding(0.1);
  },
  get_scaleband: function (arr) {
    return d3.scaleBand().range(arr).padding(0.1);
  },
  get_extent: function (data) {
    return d3.extent(data, function (d) {
      return d ? d.key : null;
    });
  },
  get_max: function (data, range) {
    return d3.max(data, function (c) {
      return d3.max(c.values, function (d) {
        if (!range || (range && range[0] <= d.key && d.key <= range[1])) return d.value;
      });
    });
  },
  get_min: function (data, range) {
    return d3.min(data, function (c) {
      return d3.min(c.values, function (d) {
        if (!range || (range && range[0] <= d.key && d.key <= range[1])) return d.value;
      });
    });
  },
  define_left_axis: function (y) {
    return d3.axisLeft(y).ticks(5, "s");
  },

  define_right_axis: function (y) {
    return d3.axisRight(y).ticks(5, "s");
  },

  define_bottom_axis: function (x, width, noTicks = false, axisTimeFormat = null) {
    return !noTicks
      ? axisTimeFormat
        ? d3
            .axisBottom(x)
            .ticks(width / 80)
            .tickSizeOuter(0)
            .tickFormat(axisTimeFormat)
        : d3
            .axisBottom(x)
            .ticks(width / 80)
            .tickSizeOuter(0)
      : d3.axisBottom(x);
  },
  define_bottom_axis_non_con: function (x, xScale, event) {
    return d3
      .axisBottom(x)
      .tickValues(
        xScale.domain().filter(function (d, i) {
          return event ? (event.transform.k > 5 ? true : !(i % 10)) : !(i % 2);
        })
      )
      .tickSizeOuter(0);
  },
  append_tooltip: function () {
    return d3.select("body").append("div").attr("class", "tooltip primaryDark--text rounded ").style("opacity", 0);
  },
  get_time_format: function () {
    return d3.timeFormat("%H:%M");
  },
  format_duration: function (value) {
    // Value in hours
    var seconds = (value && value * 60 * 60) || -1;
    // value in ms
    // var seconds = (value && value / 1000) || -1;
    return TIMESINCE(seconds);
  },
};
